import { RefObject } from "react";
import { useDataContext } from "../../../DataProvider/DataContext";
import { SelectField } from "../../formFields/SelectField";
import { swSignRequestDisabledOptions, swSignRequestOptions } from "../../../../utilities/dropDownOptions";
import { checkIfFieldIsRequiredPerArchitecture } from "../../../../utilities/architectureTypeDataProcess";
import { clearSigningIdValidationError, isSwSigningIdRequired, validateClusterValue } from "../../../../utilities/formValidation";
import { FlexColumnDiv, FlexRowDiv, StepperContentBodyDiv } from "../../../styledComponents/Components.styles";
import { textHints } from "../../../../utilities/hintText";
import { getItemByValueInDropdownOptions, isSigningAllowedForModule } from "../../../../utilities/utilityFunctions";
import palette from "../../../../utilities/palette";
import { ECUDataInterface } from "../../../../models/ECUData";
import { HeaderDiv } from "../../../styledComponents/Components.styles";
import VBFTypeLabelField from "../../formFields/VBFTypeLabelField";

const updateMachineListCluster = (prevState, machineIndex, variantIndex, clusterIndex, field, value) => {
    const newState = [...prevState];
    newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex][field] = value;
    return newState;
};

const getECUDataValue = (ECUData: RefObject<ECUDataInterface>, machineIndex, variantIndex, clusterIndex, field) => {
    return ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].clusterList[clusterIndex][field];
};

export const SwSigningDetails = ({
    machineName,
    moduleName,
    canDiagRxId,
    variantName,
    clusterEntry,
    machineIndex,
    variantIndex,
    clusterIndex,
    machineListErrors,
    setMachineListErrors,
    setMachineList,
}) => {
    const { architectureType, ECUData, dynamicSelectOptionsMap, signingAllowedModules } = useDataContext();

    const updateFieldValue = (machineIndex, variantIndex, clusterIndex, fieldName, value, pathForValidation, forceValidation = false) => {
        setMachineList(prevState => updateMachineListCluster(prevState, machineIndex, variantIndex, clusterIndex, fieldName, value));
        if (forceValidation || checkIfFieldIsRequiredPerArchitecture(pathForValidation, architectureType)) {
            validateClusterValue(machineIndex, variantIndex, clusterIndex, fieldName, value, setMachineListErrors);
        }
    };

    const handleSwSignRequestChange = (selectedOption, machineIndex, variantIndex, clusterIndex, clusterEntry) => {
        const value = selectedOption.value;

        setMachineList(prevState => updateMachineListCluster(prevState, machineIndex, variantIndex, clusterIndex, 'swSignRequest', value));

        if (checkIfFieldIsRequiredPerArchitecture(swSignRequestValidationPath, architectureType)) {
            validateClusterValue(machineIndex, variantIndex, clusterIndex, 'swSignRequest', value, setMachineListErrors);
        }

        // If value is "NO_SIGN", clear the signing ID and validation error
        if (value === "NO_SIGN") {
            updateFieldValue(machineIndex, variantIndex, clusterIndex, 'swSigningId', "", swSigningIdValidationPath, false);
            clearSigningIdValidationError(machineIndex, variantIndex, clusterIndex, setMachineListErrors);
        } 
        else if (isSigningAllowedForModule(signingAllowedModules, moduleName)) {
            // Else if signing is allowed for the module, and the new value requires a signing ID, set it to "SIGNING_ID_TRADITIONAL" by default and validate it
            if (isVBFTypeRequired(value) && clusterEntry.vbfType === "TRADITIONAL") {
                updateFieldValue(machineIndex, variantIndex, clusterIndex, 'swSigningId', "SIGNING_ID_TRADITIONAL", swSigningIdValidationPath, true);
            }
        }


    };

    const createSwSigningIdChangeHandler = (machineIndex, variantIndex, clusterIndex, clusterEntry) => (selectedOption) => {
        setMachineList(
            (prevState) => {
                const newState = [...prevState];
                newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex].swSigningId = selectedOption.value;
                return newState;
            }
        );
        if (isSwSigningIdRequired(clusterEntry.swSignRequest, clusterEntry.vbfType)) {
            validateClusterValue(
                machineIndex,
                variantIndex,
                clusterIndex,
                'swSigningId',
                selectedOption.value,
                setMachineListErrors,
            );
        }
    };

    const isVBFTypeRequired = (swSignRequestValue) => {
        return swSignRequestValue === "DEV_SIGN" || swSignRequestValue === "PROD_SIGN";
    };

    const swSignRequestValidationPath = `machineList.0.variantList.0.clusterList.0.swSignRequest`;
    const swSigningIdValidationPath = `machineList.0.variantList.0.clusterList.0.swSigningId`;

    const signingIdsOptions = (dynamicSelectOptionsMap.signingIdsOptions || []).filter(
        option => 
            option.machineName === machineName && 
            option.variantName === variantName && 
            ("0x" + option.canDiagRxId) === canDiagRxId
    );

    return (
        <>
            <div style={{color: palette.main, fontWeight: '600', fontSize: '20px', margin: '20px 0'}}>Software Signing Details</div>

            <StepperContentBodyDiv style={{ flexDirection: 'column', width: '1240px', alignSelf: 'center'}}>
                <FlexRowDiv style={{width: '1240px', alignSelf: 'center', gap: '40px', marginBottom: '20px', justifyContent: 'center'}}>
                <FlexColumnDiv>
                    <SelectField
                        label="Software Sign Request"
                        options={isSigningAllowedForModule(signingAllowedModules, moduleName) ? swSignRequestOptions : swSignRequestDisabledOptions}
                        required={checkIfFieldIsRequiredPerArchitecture(`machineList.0.variantList.0.clusterList.0.swSignRequest`, architectureType)}
                        value={isSigningAllowedForModule(signingAllowedModules, moduleName) ? getItemByValueInDropdownOptions(swSignRequestOptions, clusterEntry.swSignRequest) : 
                            getItemByValueInDropdownOptions(swSignRequestDisabledOptions, clusterEntry.swSignRequest)
                        }
                        placeholder="Select Software Sign Request"
                        textHint={textHints.swSignRequest}
                        onChange={(selectedOption) => handleSwSignRequestChange(selectedOption, machineIndex, variantIndex, clusterIndex, clusterEntry)}
                        errorMessage={machineListErrors[machineIndex]?.variantListErrors[variantIndex]?.clusterListErrors[clusterIndex]?.swSignRequestValidationError}
                        pathToField={`machineList.0.variantList.0.clusterList.0.swSignRequest`}
                        stylingVariant="body"
                        isPrefilledValueChanged={clusterEntry.swSignRequest !== getECUDataValue(ECUData, machineIndex, variantIndex, clusterIndex, 'swSignRequest')}
                    />

                    {isSwSigningIdRequired(clusterEntry.swSignRequest, clusterEntry.vbfType) && (
                    <SelectField
                        label="Signing ID"
                        options={signingIdsOptions}
                        required={true}  
                        value={getItemByValueInDropdownOptions(signingIdsOptions, clusterEntry.swSigningId)}
                        placeholder={`Select Signing Id`}
                        textHint={textHints.swSigningId}
                        onChange={createSwSigningIdChangeHandler(machineIndex, variantIndex, clusterIndex, clusterEntry)}
                        errorMessage={machineListErrors[machineIndex]?.variantListErrors[variantIndex]?.clusterListErrors[clusterIndex]?.swSigningIdValidationError}
                        pathToField={`machineList.0.variantList.0.clusterList.0.swSigningId`}
                        stylingVariant="body"
                        isPrefilledValueChanged={clusterEntry.swSigningId !== getECUDataValue(ECUData, machineIndex, variantIndex, clusterIndex, 'swSigningId')}
                    />
                    )}
                    
                </FlexColumnDiv>
                <FlexColumnDiv>
                    {isVBFTypeRequired(clusterEntry.swSignRequest) ? (
                    <div style={{width: '560px'}}>
                        <VBFTypeLabelField 
                            label="VBF Type"
                            value={(clusterEntry.vbfType || "N/A")}
                            errorMessage={machineListErrors[machineIndex]?.variantListErrors[variantIndex]?.clusterListErrors[clusterIndex]?.vbfTypeValidationError}
                            isLoading={clusterEntry.isProcessingSignFileMetadata === true}
                        />
                    </div>)
                    : 
                        <div style={{width: '560px'}}></div>
                    }

                </FlexColumnDiv>
                </FlexRowDiv>
                {isSwSigningIdRequired(clusterEntry.swSignRequest, clusterEntry.vbfType) && signingIdsOptions.length === 0 && (
                    <>
                        <HeaderDiv style={{color: palette.stepper.warning, fontWeight: 'bold', alignSelf: 'start'}}>
                            No Signing IDs found for this machine variant.
                        </HeaderDiv>   
                        <HeaderDiv style={{color: palette.stepper.warning, alignSelf: 'start'}}>
                            Please work with IVSS team to setup your Signing ID. For more information, contact <a href="mailto:ivss@ford.com">ivss@ford.com</a>.
                        </HeaderDiv>
                    </>
                )}

            </StepperContentBodyDiv>
        </>
    );
};
